import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../lib/firebase/config';
import FormInput from '../components/auth/FormInput';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

export default function ForgotPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      setIsSuccess(false);
      console.log('[ForgotPassword] Attempting to send password reset email for:', data.email);
      if (!data.email) {
        setError('Please enter your email address.');
        setIsLoading(false);
        return;
      }
      await sendPasswordResetEmail(auth, data.email);
      setIsSuccess(true);
      console.log('[ForgotPassword] Password reset email sent successfully.');
    } catch (err) {
      console.error('[ForgotPassword] Error sending password reset email:', err);
      let message = 'An error occurred. Please try again.';
      if (err && err.code === 'auth/user-not-found') {
        message = 'No account found with this email address.';
      } else if (err && err.code === 'auth/invalid-email') {
        message = 'Invalid email address.';
      } else if (err && err.code === 'auth/network-request-failed') {
        message = 'Network error. Please check your connection and try again.';
      } else if (err && err.message) {
        message += ` [${err.message}]`;
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Reset your password</h2>

      {isSuccess ? (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Password reset email sent
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  We've sent a password reset link to your email address. Please check
                  your inbox and follow the instructions to reset your password.
                </p>
              </div>
              <div className="mt-4">
                <a
                  href="/login"
                  className="text-sm font-medium text-green-600 hover:text-green-500"
                >
                  Return to sign in
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-6">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <FormInput
              label="Email address"
              id="email"
              type="email"
              autoComplete="email"
              error={errors.email?.message}
              {...register('email')}
            />

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orrekie-600 hover:bg-orrekie-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orrekie-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending reset link...' : 'Send reset link'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Remember your password?
                </span>
              </div>
            </div>

            <div className="mt-6">
              <a
                href="/login"
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orrekie-500"
              >
                Sign in
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
} 